import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import authService from "../../services/authService";
import {
  TrendingUp, TrendingDown, Search, Filter, RefreshCw,
  ShoppingBag, BarChart3, Star, ArrowUpRight, ArrowDownRight,
  Zap, Package, DollarSign, Loader2
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TrendsDashboard = () => {
  const [selectedMarketplace, setSelectedMarketplace] = useState('all');
  const [loading, setLoading] = useState(true);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [categoryTrends, setCategoryTrends] = useState([]);
  const [marketplaceStats, setMarketplaceStats] = useState([]);

  const marketplaces = [
    { id: 'all', name: 'Todos' },
    { id: 'mercadolivre', name: 'Mercado Livre' },
    { id: 'shopee', name: 'Shopee' },
    { id: 'amazon', name: 'Amazon' },
    { id: 'magalu', name: 'Magalu' },
    { id: 'americanas', name: 'Americanas' },
    { id: 'shein', name: 'Shein' },
  ];

  const trendData = [
    { hour: '08h', ml: 1200, shopee: 800, amazon: 600 },
    { hour: '10h', ml: 2100, shopee: 1500, amazon: 1100 },
    { hour: '12h', ml: 2800, shopee: 2200, amazon: 1800 },
    { hour: '14h', ml: 3500, shopee: 2800, amazon: 2200 },
    { hour: '16h', ml: 4200, shopee: 3500, amazon: 2800 },
    { hour: '18h', ml: 5100, shopee: 4200, amazon: 3500 },
    { hour: '20h', ml: 6200, shopee: 5000, amazon: 4100 },
    { hour: '22h', ml: 5500, shopee: 4500, amazon: 3800 },
  ];

  useEffect(() => { loadData(); }, [selectedMarketplace]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prodResp, catResp, mpResp] = await Promise.all([
        authService.fetchAuth(`${BACKEND_URL}/api/tendencias/produtos?marketplace=${selectedMarketplace}&limit=15`),
        authService.fetchAuth(`${BACKEND_URL}/api/tendencias/categorias`),
        authService.fetchAuth(`${BACKEND_URL}/api/tendencias/marketplace-stats`)
      ]);

      if (prodResp.ok) {
        const d = await prodResp.json();
        setTrendingProducts(d.produtos || []);
      }
      if (catResp.ok) {
        const d = await catResp.json();
        setCategoryTrends(d.categorias || []);
      }
      if (mpResp.ok) {
        const d = await mpResp.json();
        setMarketplaceStats(d.marketplaces || []);
      }
    } catch (error) {
      console.error('Erro ao carregar tendencias:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3 lg:p-4 bg-gray-950 overflow-auto h-full">
      {/* Header */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full uppercase">Tempo Real</span>
              <h1 className="text-lg font-bold text-white">Tendências de Mercado</h1>
            </div>
            <p className="text-[11px] text-gray-400">Dados reais de tendências dos principais marketplaces</p>
          </div>
          <Button size="sm" className="bg-blue-500 hover:bg-blue-600 h-7 text-xs" onClick={loadData}>
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Atualizar
          </Button>
        </div>
      </div>

      {/* Marketplace Filter */}
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {marketplaces.map((mp) => (
          <Button key={mp.id} size="sm" onClick={() => setSelectedMarketplace(mp.id)}
            className={`h-7 text-xs whitespace-nowrap ${selectedMarketplace === mp.id ? 'bg-blue-500' : 'bg-gray-700 hover:bg-gray-600'}`}>
            {mp.name}
          </Button>
        ))}
      </div>

      {/* Marketplace Stats */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        {marketplaceStats.slice(0, 6).map((mp) => (
          <Card key={mp.nome} className="bg-gray-900/50 border-gray-800 p-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-white text-xs font-medium truncate">{mp.nome}</span>
              <span className={`text-[10px] font-bold ${mp.crescimento > 0 ? 'text-green-400' : 'text-red-400'}`}>
                {mp.crescimento > 0 ? '+' : ''}{mp.crescimento}%
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              <div><p className="text-[9px] text-gray-400">Buscas</p><p className="text-white text-xs font-bold">{mp.buscas}</p></div>
              <div><p className="text-[9px] text-gray-400">Produtos</p><p className="text-white text-xs font-bold">{mp.produtos}</p></div>
            </div>
          </Card>
        ))}
      </div>

      {/* Grafico + Categorias */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <Card className="bg-gray-900/50 border-gray-800 p-3 lg:col-span-2">
          <h3 className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-blue-400" />Volume de Buscas por Hora
          </h3>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="hour" stroke="#9ca3af" fontSize={10} />
                <YAxis stroke="#9ca3af" fontSize={10} />
                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px', fontSize: '10px' }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Line type="monotone" dataKey="ml" name="Mercado Livre" stroke="#fbbf24" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="shopee" name="Shopee" stroke="#f97316" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="amazon" name="Amazon" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="bg-gray-900/50 border-gray-800 p-3">
          <h3 className="text-sm font-semibold text-white mb-2">Categorias em Alta</h3>
          <div className="space-y-2">
            {categoryTrends.slice(0, 6).map((cat) => (
              <div key={cat.nome} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-16 bg-gray-700 rounded-full h-1.5">
                    <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full" style={{ width: `${Math.min(cat.percentual || 0, 100)}%` }} />
                  </div>
                  <span className="text-white text-xs">{cat.nome}</span>
                </div>
                <span className={`text-[10px] font-bold ${cat.crescimento > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {cat.crescimento > 0 ? '+' : ''}{cat.crescimento}%
                </span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Produtos em Tendencia */}
      <Card className="bg-gray-900/50 border-gray-800 p-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />Produtos em Tendencia
          </h3>
          <span className="text-[10px] text-gray-400">{trendingProducts.length} produtos</span>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {trendingProducts.map((product) => (
              <div key={product.rank} className="p-2 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-orange-500/50 transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                      product.rank === 1 ? 'bg-yellow-500' : product.rank === 2 ? 'bg-gray-400' : product.rank === 3 ? 'bg-orange-600' : 'bg-gray-600'
                    }`}>{product.rank}</span>
                    <div>
                      <p className="text-white text-xs font-medium">{product.nome}</p>
                      <p className="text-gray-400 text-[10px]">{product.categoria}</p>
                    </div>
                  </div>
                  <span className="text-green-400 text-[10px] font-bold flex items-center"><ArrowUpRight className="w-3 h-3" />+{product.crescimento}%</span>
                </div>
                <div className="flex items-center gap-1 mb-1.5">
                  <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1 rounded">{product.fornecedor}</span>
                  <span className="text-[9px] bg-green-500/20 text-green-400 px-1 rounded">{product.estoque} un.</span>
                  {product.tem_no_catalogo && <span className="text-[9px] bg-orange-500/20 text-orange-400 px-1 rounded">No catalogo</span>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <div>
                    <p className="text-gray-400">Buscas</p>
                    <p className="text-white font-bold">{(product.buscas / 1000).toFixed(0)}K</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Faixa</p>
                    <p className="text-green-400 font-bold">{product.preco_faixa}</p>
                  </div>
                  <div>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-5 text-[9px] w-full px-1"
                      onClick={() => toast.success(`${product.nome} selecionado! Va em "Meu Painel" para publicar.`)}>
                      <Zap className="w-2.5 h-2.5 mr-0.5" />Vender
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dica */}
      <div className="mt-3 p-2.5 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-lg">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-400" />
          <div>
            <p className="text-blue-300 text-xs font-semibold">Dica de Ouro</p>
            <p className="text-gray-400 text-[10px]">
              {trendingProducts[0] ? `"${trendingProducts[0].nome}" esta com +${trendingProducts[0].crescimento}% de buscas! Aproveite para publicar.` : 'Carregando tendencias...'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendsDashboard;
